import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  CheckSquare, FileCheck, Receipt, AlertCircle, Trash2, ExternalLink,
  CheckCheck, Trash, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORIES = [
  { key: 'all', label: 'All', icon: Inbox },
  { key: 'unread', label: 'Unread', icon: AlertCircle },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare },
  { key: 'approvals', label: 'Approvals', icon: FileCheck },
  { key: 'invoices', label: 'Invoices', icon: Receipt },
];

const CATEGORY_ICON = {
  tasks: CheckSquare, approvals: FileCheck, invoices: Receipt, system: AlertCircle,
};

const CATEGORY_COLOR = {
  tasks: 'text-blue-400 border-blue-500/30 bg-blue-500/10',
  approvals: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  invoices: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
  system: 'text-muted-foreground border-border bg-muted/20',
};

function formatRelative(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

export function NotificationsModal({ open, onOpenChange, notifications, setNotifications, onOpenClipboard }) {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [confirmClearAll, setConfirmClearAll] = useState(false);
  const [confirmClearRead, setConfirmClearRead] = useState(false);

  const counts = useMemo(() => {
    const c = { all: notifications.length, unread: 0, tasks: 0, approvals: 0, invoices: 0 };
    notifications.forEach(n => {
      if (!n.is_read) c.unread += 1;
      if (c[n.category] !== undefined) c[n.category] += 1;
    });
    return c;
  }, [notifications]);

  const filtered = useMemo(() => {
    if (activeCategory === 'all') return notifications;
    if (activeCategory === 'unread') return notifications.filter(n => !n.is_read);
    return notifications.filter(n => n.category === activeCategory);
  }, [notifications, activeCategory]);

  const markRead = async (nid) => {
    setNotifications(prev => prev.map(n => n.id === nid ? { ...n, is_read: true } : n));
    api.put(`/notifications/${nid}/read`).catch(() => {});
  };

  const handleOpen = async (n) => {
    if (!n.is_read) await markRead(n.id);
    // Sticky notifications open the clipboard modal in-place instead of navigating
    if (n.entity_type === 'sticky_received' || n.entity_type === 'sticky_warning') {
      onOpenChange(false);
      onOpenClipboard?.();
      return;
    }
    if (n.link_url) {
      onOpenChange(false);
      navigate(n.link_url);
    }
  };

  const handleClear = async (nid) => {
    setNotifications(prev => prev.filter(n => n.id !== nid));
    try { await api.delete(`/notifications/${nid}`); } catch { toast.error('Could not clear'); }
  };

  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await api.put('/notifications/read-all');
      toast.success('All marked as read');
    } catch { toast.error('Could not mark all read'); }
  };

  const clearAllRead = async () => {
    try {
      const r = await api.delete('/notifications/clear-read');
      setNotifications(prev => prev.filter(n => !n.is_read));
      toast.success(`Cleared ${r.data.deleted} read notification${r.data.deleted === 1 ? '' : 's'}`);
      setConfirmClearRead(false);
    } catch { toast.error('Could not clear read'); }
  };

  const clearAll = async () => {
    try {
      const r = await api.delete('/notifications/clear-all');
      setNotifications([]);
      toast.success(`Cleared ${r.data.deleted} notification${r.data.deleted === 1 ? '' : 's'}`);
      setConfirmClearAll(false);
    } catch { toast.error('Could not clear all'); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-3xl p-0 gap-0 overflow-hidden" data-testid="notifications-modal">
          <DialogHeader className="px-5 py-3 border-b border-border">
            <DialogTitle className="text-base flex items-center gap-2">
              <Inbox className="h-4 w-4 text-primary" />
              Notifications
              {counts.unread > 0 && (
                <Badge className="bg-primary/20 text-primary border-primary/40 text-xs ml-1" data-testid="notif-modal-unread-badge">
                  {counts.unread} unread
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex h-[480px]">
            {/* Filter rail */}
            <div className="w-44 border-r border-border bg-muted/10 p-2 flex flex-col gap-0.5">
              {CATEGORIES.map(c => {
                const Icon = c.icon;
                const isActive = activeCategory === c.key;
                const count = counts[c.key];
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveCategory(c.key)}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md text-xs font-medium transition ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground'}`}
                    data-testid={`notif-filter-${c.key}`}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5" />
                      {c.label}
                    </span>
                    <span className={`text-xs ${isActive ? 'text-primary' : 'text-muted-foreground/60'}`}>{count}</span>
                  </button>
                );
              })}

              <div className="mt-auto pt-3 border-t border-border space-y-1">
                <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-7 text-muted-foreground hover:text-foreground"
                  onClick={markAllRead} disabled={counts.unread === 0} data-testid="notif-mark-all-read">
                  <CheckCheck className="h-3 w-3 mr-1.5" /> Mark all read
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-7 text-muted-foreground hover:text-foreground"
                  onClick={() => setConfirmClearRead(true)} disabled={notifications.filter(n => n.is_read).length === 0} data-testid="notif-clear-read">
                  <Trash className="h-3 w-3 mr-1.5" /> Clear read
                </Button>
                <Button variant="ghost" size="sm" className="w-full justify-start text-xs h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setConfirmClearAll(true)} disabled={notifications.length === 0} data-testid="notif-clear-all">
                  <Trash2 className="h-3 w-3 mr-1.5" /> Clear all
                </Button>
              </div>
            </div>

            {/* List */}
            <ScrollArea className="flex-1">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <Inbox className="h-10 w-10 text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {activeCategory === 'unread' ? "You're all caught up." : 'No notifications here.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filtered.map(n => {
                    const Icon = CATEGORY_ICON[n.category] || AlertCircle;
                    return (
                      <div
                        key={n.id}
                        className={`flex gap-3 px-4 py-3 group transition ${!n.is_read ? 'bg-primary/[0.04]' : ''}`}
                        data-testid={`notif-row-${n.id}`}
                      >
                        <div className={`shrink-0 h-7 w-7 rounded-md border flex items-center justify-center mt-0.5 ${CATEGORY_COLOR[n.category] || CATEGORY_COLOR.system}`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-relaxed whitespace-pre-wrap break-words ${!n.is_read ? 'text-foreground font-medium' : 'text-foreground/80'}`}>
                            {n.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-muted-foreground">{formatRelative(n.created_at)}</span>
                            {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {n.link_url && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleOpen(n)} data-testid={`notif-open-${n.id}`}>
                              <ExternalLink className="h-3 w-3 mr-1" /> Open
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleClear(n.id)} data-testid={`notif-clear-${n.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Clear Read */}
      <AlertDialog open={confirmClearRead} onOpenChange={setConfirmClearRead}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all read notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the {notifications.filter(n => n.is_read).length} read notification{notifications.filter(n => n.is_read).length === 1 ? '' : 's'} from your inbox. Unread items will be preserved. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearAllRead} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-clear-read">
              Clear read
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Clear All */}
      <AlertDialog open={confirmClearAll} onOpenChange={setConfirmClearAll}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all {notifications.length} notification{notifications.length === 1 ? '' : 's'} from your inbox. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-clear-all">
              Clear all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
